/**
 * Copyright (c) 2016-present GlossGenius. All rights reserved.
 */

import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import validator from 'validator';
import * as yup from 'yup';

import { useFeatureFlag } from '@glossgenius/core-features-react-sdk';

import Button from 'components/Button';
import { InputField } from 'components/InputField/InputField';
import { RadioButtons } from 'components/RadioButtons';
import { StripeElement } from 'components/StripeElement';

import { FEATURE_FLAGS } from 'core-features-config/activeFeatureFlags';

import { useBusiness } from 'hooks/business/useBusiness';
import { useGiftCards } from 'hooks/gift-cards/useGiftCards';
import { useWebsite } from 'hooks/website/useWebsite';

import { TGiftCardForm } from 'types/gift-card';

import { cleanPhone } from 'utils/helpers';

import {
  GIFT_CARD_RECIPIENT_VALUES,
  PRESET_GIFT_CARD_VALUES,
} from './constants';

import s from './GiftCard.module.css';

const MIN_GIFT_CARD_AMOUNT = 10; // $10
const MAX_GIFT_CARD_AMOUNT = 500; // $500

const GiftCardSection = ({
  title,
  children,
}: React.PropsWithChildren & { title: string }) => {
  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
};

export const GiftCard = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { name } = useBusiness();
  const { slug } = useWebsite();

  const {
    giftCard,
    resetGiftCardState,
    startGiftCardFormLoading,
    stopGiftCardFormLoading,
    purchaseGiftCard,
  } = useGiftCards();

  const improvementFF = useFeatureFlag(FEATURE_FLAGS.GIFT_CARD_IMPROVEMENT);
  const [stripeHasFocus, setStripeHasFocus] = React.useState(false);
  const [stripeError, setStripeError] = React.useState<string | undefined>(
    undefined,
  );
  const [cardInputCompleted, setCardInputCompleted] = React.useState(false);

  React.useEffect(() => {
    resetGiftCardState();

    // Don't put this function in the deps!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStripeChange = (event: StripeCardElementChangeEvent) => {
    setStripeError(event.error?.message);
    setCardInputCompleted(event.complete);
    if (!event.error && event.complete) {
      setStripeError(undefined);
    }
  };

  const onStripeBlur = () => {
    if (!cardInputCompleted) {
      setStripeError('Your Credit Card information is incompleted');
    }
    setStripeHasFocus(false);
  };

  const handleSubmitClick = () => {
    if (!cardInputCompleted) {
      setStripeError('Your Credit Card information is incompleted');
    }
  };

  const onSubmit = async ({
    client_email,
    client_name,
    client_phone,
    sender_email,
    sender_name,
    value,
    preset_value,
    recipient,
  }: TGiftCardForm) => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    startGiftCardFormLoading();
    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);

    const isBuyingForOther = improvementFF.isEnabled && recipient === 'other';
    const senderName = isBuyingForOther ? sender_name : undefined;
    const senderEmail = isBuyingForOther ? sender_email?.trim() : undefined;
    const stripeName = isBuyingForOther ? sender_name : client_name;

    if (cardElement) {
      const { token, error } = await stripe.createToken(cardElement, {
        name: stripeName,
      });

      if (error) {
        setStripeError(error?.message);

        stopGiftCardFormLoading();
      } else {
        setStripeError(undefined);
        // In case of no error, loading state is handled by `purchaseGiftCard` method.
        // The spinner becomes hidden whenever this method returns success or failure.
        purchaseGiftCard({
          client_name,
          client_email: client_email.trim(),
          client_phone: cleanPhone(client_phone).substring(1),
          sender_name: senderName,
          sender_email: senderEmail,
          value: Number(preset_value) || Number(value) * 100, // value is in dollars, preset_value is in cents, and stripe expects cents
          slug,
          token: token.id,
        });
      }
    } else {
      setStripeError('There was an error submitting your card information');
    }
  };

  const giftCardSchema = yup
    .object({
      client_email: yup
        .string()
        .required('Please enter Email address')
        .test('is-email', 'Please enter a valid email address', (value) =>
          validator.isEmail(value),
        ),
      client_name: yup.string().required('Please enter Name'),
      client_phone: yup
        .string()
        .transform((value) => cleanPhone(value))
        .min(11, 'Phone number must be 10 or 11 digits')
        .required('Please enter Phone')
        .test('is-james', 'Please enter a valid phone number', (value) =>
          validator.isMobilePhone(value, 'en-US'),
        ),
      sender_name: yup.string().when(['recipient'], ([recipient], schema) => {
        if (recipient === 'other') {
          return schema.required("Please enter Sender's Name");
        }
        return schema;
      }),
      sender_email: yup.string().when(['recipient'], ([recipient], schema) => {
        if (recipient === 'other') {
          return schema
            .required('Please enter Email address')
            .test('is-email', 'Please enter a valid email address', (value) => {
              return validator.isEmail(value);
            });
        }
        return schema;
      }),
      preset_value: yup
        .string()
        .required('Please select an amount or enter a custom value'),
      value: yup.string().when('preset_value', ([presetValue], schema) => {
        if (presetValue !== '0') {
          return schema.nullable();
        }
        return schema
          .transform((value) => value?.replace('$', '')?.trim())
          .test(
            'min',
            `Amount cannot be less than $${MIN_GIFT_CARD_AMOUNT}`,
            (value) => Number(value) >= MIN_GIFT_CARD_AMOUNT,
          )
          .test(
            'max',
            `Amount cannot be more than $${MAX_GIFT_CARD_AMOUNT}`,
            (value) => Number(value) <= MAX_GIFT_CARD_AMOUNT,
          );
      }),
      recipient: improvementFF.isEnabled
        ? yup.string().required('Please select a recipient')
        : yup.string(),
    })
    .required();

  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    resetField,
  } = useForm<TGiftCardForm>({
    resolver: yupResolver(giftCardSchema),
    mode: 'onBlur',
  });
  const presetValue = useWatch({
    control,
    name: 'preset_value',
  });
  const recipient = useWatch({
    control,
    name: 'recipient',
  });
  const [showCustomAmount, setShowCustomAmount] = React.useState(false);

  React.useEffect(() => {
    if (presetValue && Number(presetValue) === 0) {
      setShowCustomAmount(true);
    } else {
      setShowCustomAmount(false);
      resetField('value');
    }
  }, [presetValue, resetField]);

  const renderForm = () => (
    <div className={s.root}>
      <div className={s.pageTitle}>Purchase A Gift Card</div>
      <div
        className={s.pageSubtitle}
      >{`You can purchase gift cards from ${name} here`}</div>

      <form
        noValidate
        className={s.form}
        onSubmit={(event) => void handleSubmit(onSubmit)(event)}
      >
        <GiftCardSection title="Choose an amount">
          <RadioButtons
            id="preset_value"
            options={PRESET_GIFT_CARD_VALUES}
            required
            error={errors.preset_value?.message}
            register={register('preset_value')}
          />
          {showCustomAmount && (
            <InputField
              id="value"
              type="price"
              label="Custom amount"
              placeholder="Enter amount"
              name="client_name"
              register={register('value')}
              decimalScale={0}
              error={errors.value?.message}
              required
            />
          )}
        </GiftCardSection>

        {improvementFF.isEnabled ? (
          <>
            <GiftCardSection title="Who's this gift card for?">
              <RadioButtons
                id="recipient"
                options={GIFT_CARD_RECIPIENT_VALUES}
                required
                error={errors.recipient?.message}
                register={register('recipient')}
              />
              {!!recipient && (
                <>
                  <InputField
                    id="client_name"
                    label={recipient === 'self' ? 'Name' : "Recipient's name"}
                    name="client_name"
                    register={register('client_name')}
                    error={errors.client_name?.message}
                    required
                  />
                  <InputField
                    id="client_email"
                    label={recipient === 'self' ? 'Email' : "Recipient's email"}
                    name="client_email"
                    register={register('client_email')}
                    error={errors.client_email?.message}
                    required
                  />
                  <InputField
                    id="client_phone"
                    label={recipient === 'self' ? 'Phone' : "Recipient's phone"}
                    type="tel"
                    name="client_phone"
                    control={control}
                    error={errors.client_phone?.message}
                    required
                  />
                </>
              )}
            </GiftCardSection>
            {recipient === 'other' && (
              <GiftCardSection title="Who's it from?">
                <InputField
                  id="sender_name"
                  label="Name"
                  name="sender_name"
                  register={register('sender_name')}
                  error={errors.sender_name?.message}
                />
                <InputField
                  id="sender_email"
                  label="Email"
                  name="sender_email"
                  register={register('sender_email')}
                  error={errors.sender_email?.message}
                />
              </GiftCardSection>
            )}
            <GiftCardSection title="Pay with credit card">
              <StripeElement
                id="credit-card"
                onChange={onStripeChange}
                onFocus={() => setStripeHasFocus(true)}
                onBlur={onStripeBlur}
                errorMessage={stripeError}
                hasFocus={stripeHasFocus}
                hiddenLabel
              />
            </GiftCardSection>
          </>
        ) : (
          <>
            <InputField
              id="client_name"
              label={'Your Name'}
              name="client_name"
              register={register('client_name')}
              error={errors.client_name?.message}
              required
            />
            <InputField
              id="client_email"
              label={'Your Email'}
              name="client_email"
              register={register('client_email')}
              error={errors.client_email?.message}
              required
            />
            <InputField
              id="client_phone"
              label={'Phone'}
              type="tel"
              name="client_phone"
              control={control}
              error={errors.client_phone?.message}
              required
            />

            <StripeElement
              id="credit-card"
              onChange={onStripeChange}
              onFocus={() => setStripeHasFocus(true)}
              onBlur={onStripeBlur}
              errorMessage={stripeError}
              hasFocus={stripeHasFocus}
            />
          </>
        )}

        {giftCard.error ? (
          <div className={s.error} data-error="gift-card-error">
            {giftCard.error}
          </div>
        ) : (
          <div />
        )}

        <Button
          className={s.submitButton}
          loading={giftCard.loading}
          disabled={giftCard.loading || !stripe || !elements}
          onClick={handleSubmitClick}
        >
          Purchase Now
        </Button>
      </form>
    </div>
  );

  if (giftCard.success) {
    return (
      <div className={s.root}>
        <div>
          <h1 className={s.pageTitle}>Your Purchase is Complete!</h1>
          <div className={s.pageSubtitle}>
            You will be receiving an email in the next 5-10 minutes with your
            gift card. Thank you!
          </div>
        </div>
      </div>
    );
  }
  return renderForm();
};
